/* Sunshine and Ski — winter home page behaviours (ss-*). Safe to include more than once. */
(() => {
  if (window.__ssWinter) return;
  window.__ssWinter = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Snowfall canvas ---------- */
  class SsSnow extends HTMLElement {
    connectedCallback() {
      if (reduceMotion) return;
      this.canvas = document.createElement('canvas');
      this.canvas.setAttribute('aria-hidden', 'true');
      this.appendChild(this.canvas);
      this.ctx = this.canvas.getContext('2d');
      this.density = Number(this.dataset.density || 110);
      this.flakes = [];
      this.wind = 0;
      this.visible = true;
      this.resize = this.resize.bind(this);
      this.tick = this.tick.bind(this);
      this.resize();
      window.addEventListener('resize', this.resize, { passive: true });
      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible && !this.raf) this.raf = requestAnimationFrame(this.tick);
      });
      this.io.observe(this);
      this.raf = requestAnimationFrame(this.tick);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.resize);
      if (this.io) this.io.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
    }

    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const { width, height } = this.getBoundingClientRect();
      this.w = width;
      this.h = height;
      this.canvas.width = width * dpr;
      this.canvas.height = height * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const target = Math.round((this.density * width) / 1440);
      while (this.flakes.length < target) this.flakes.push(this.make(true));
      this.flakes.length = target;
    }

    make(anywhere) {
      const depth = Math.random();
      return {
        x: Math.random() * this.w,
        y: anywhere ? Math.random() * this.h : -10,
        r: 0.6 + depth * 2.6,
        vy: 0.25 + depth * 1.1,
        phase: Math.random() * Math.PI * 2,
        sway: 0.2 + Math.random() * 0.6,
        a: 0.35 + depth * 0.6,
      };
    }

    tick(t) {
      if (!this.visible) {
        this.raf = null;
        return;
      }
      const { ctx } = this;
      ctx.clearRect(0, 0, this.w, this.h);
      this.wind = Math.sin(t / 4000) * 0.35;
      for (const f of this.flakes) {
        f.y += f.vy;
        f.x += Math.sin(t / 1200 + f.phase) * f.sway * 0.4 + this.wind * f.vy;
        if (f.y > this.h + 10) Object.assign(f, this.make(false));
        if (f.x > this.w + 10) f.x = -10;
        if (f.x < -10) f.x = this.w + 10;
        ctx.beginPath();
        ctx.fillStyle = `rgba(255,255,255,${f.a})`;
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fill();
      }
      this.raf = requestAnimationFrame(this.tick);
    }
  }
  if (!customElements.get('ss-snow')) customElements.define('ss-snow', SsSnow);

  /* ---------- Hero parallax (mountain layers) ---------- */
  class SsParallax extends HTMLElement {
    connectedCallback() {
      if (reduceMotion) return;
      this.layers = [...this.querySelectorAll('[data-depth]')];
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y < window.innerHeight * 1.2) {
          for (const el of this.layers) {
            el.style.transform = `translate3d(0, ${y * Number(el.dataset.depth)}px, 0)`;
          }
        }
        this.ticking = false;
      });
    }
  }
  if (!customElements.get('ss-parallax')) customElements.define('ss-parallax', SsParallax);

  /* ---------- Countdown ---------- */
  class SsCountdown extends HTMLElement {
    connectedCallback() {
      const raw = this.dataset.target;
      this.target = raw ? new Date(`${raw}T00:00:00`).getTime() : NaN;
      if (Number.isNaN(this.target)) {
        this.hidden = true;
        return;
      }
      this.nums = {
        d: this.querySelector('[data-unit="d"]'),
        h: this.querySelector('[data-unit="h"]'),
        m: this.querySelector('[data-unit="m"]'),
        s: this.querySelector('[data-unit="s"]'),
      };
      this.update();
      this.timer = setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    update() {
      const diff = this.target - Date.now();
      if (diff <= 0) {
        clearInterval(this.timer);
        const units = this.querySelector('.ss-countdown__units');
        const done = this.querySelector('.ss-countdown__done');
        if (units) units.hidden = true;
        if (done) done.hidden = false;
        return;
      }
      const pad = (n) => String(n).padStart(2, '0');
      this.nums.d.textContent = Math.floor(diff / 86400000);
      this.nums.h.textContent = pad(Math.floor(diff / 3600000) % 24);
      this.nums.m.textContent = pad(Math.floor(diff / 60000) % 60);
      this.nums.s.textContent = pad(Math.floor(diff / 1000) % 60);
    }
  }
  if (!customElements.get('ss-countdown')) customElements.define('ss-countdown', SsCountdown);

  /* ---------- Tabs (WAI-ARIA tabs pattern) ---------- */
  class SsTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          let next = null;
          if (e.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
          if (e.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = this.tabs.length - 1;
          if (next !== null) {
            e.preventDefault();
            this.select(next);
            this.tabs[next].focus();
          }
        });
      });
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', on);
        tab.tabIndex = on ? 0 : -1;
        this.panels[i].hidden = !on;
      });
      const rail = this.panels[index].querySelector('ss-rail');
      if (rail && rail.refresh) rail.refresh();
    }
  }
  if (!customElements.get('ss-tabs')) customElements.define('ss-tabs', SsTabs);

  /* ---------- Horizontal rail with arrow buttons ---------- */
  class SsRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ss-rail__track');
      this.prev = this.querySelector('.ss-rail__btn--prev');
      this.next = this.querySelector('.ss-rail__btn--next');
      if (!this.track) return;
      this.refresh = this.refresh.bind(this);
      const step = (dir) => this.track.scrollBy({ left: dir * this.track.clientWidth * 0.85, behavior: 'smooth' });
      if (this.prev) this.prev.addEventListener('click', () => step(-1));
      if (this.next) this.next.addEventListener('click', () => step(1));
      this.track.addEventListener('scroll', this.refresh, { passive: true });
      window.addEventListener('resize', this.refresh, { passive: true });
      this.refresh();
    }

    refresh() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    }
  }
  if (!customElements.get('ss-rail')) customElements.define('ss-rail', SsRail);

  /* ---------- Snow watch: live ECCC forecasts per resort ---------- */
  const ECCC = 'https://api.weather.gc.ca/collections/citypageweather-realtime/items/';
  const CACHE_MINUTES = 30;
  const ICONS = {
    snow: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><g stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9"/></g></svg>',
    rain: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7 15a4 4 0 0 1 .5-8 5 5 0 0 1 9.6 1.5A3.5 3.5 0 0 1 17 15H7z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M9 18l-1 2M13 18l-1 2M17 18l-1 2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
    sun: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.6"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
    cloud: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7 18a4.5 4.5 0 0 1 .5-9 5.5 5.5 0 0 1 10.6 1.7A3.8 3.8 0 0 1 17.5 18H7z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>',
  };
  const kind = (text) => {
    const t = (text || '').toLowerCase();
    if (/snow|flurr|blizzard|ice pellet/.test(t)) return 'snow';
    if (/rain|shower|drizzle|thunder/.test(t)) return 'rain';
    if (/sun|clear/.test(t)) return 'sun';
    return 'cloud';
  };
  const en = (v) => (v && typeof v === 'object' && 'en' in v ? v.en : v);
  const esc = (v) => String(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const parse = (props) => {
    const forecasts = (props.forecastGroup && props.forecastGroup.forecasts) || [];
    let snow48 = 0;
    let snowPeriods = 0;
    let snowSoon = false;
    let firstSnow = '';
    const days = [];
    forecasts.forEach((f, i) => {
      const summary = en(f.abbreviatedForecast && f.abbreviatedForecast.textSummary) || en(f.cloudPrecip) || '';
      const acc = f.precipitation && f.precipitation.accumulation;
      const accName = acc && en(acc.name);
      const accUnits = acc && acc.amount && en(acc.amount.units);
      const accVal = acc && acc.amount ? Number(en(acc.amount.value)) || 0 : 0;
      if (accName && /snow/i.test(accName) && i < 4) snow48 += accUnits === 'mm' ? accVal / 10 : accVal;
      const name = en(f.period && f.period.textForecastName) || '';
      if (kind(summary) === 'snow') {
        snowPeriods += 1;
        if (i < 4) snowSoon = true;
        if (!firstSnow) firstSnow = name;
      }
      if (!/night/i.test(name) && days.length < 5) {
        const temps = (f.temperatures && f.temperatures.temperature) || [];
        const high = temps.find((t) => en(t.class) === 'high') || temps[0];
        days.push({ name: name === 'Today' ? 'Today' : name.slice(0, 3), temp: high ? Math.round(Number(en(high.value))) : null, kind: kind(summary), summary });
      }
    });
    const cc = props.currentConditions || {};
    const temp = cc.temperature ? Number(en(cc.temperature.value)) : null;
    const first = forecasts[0];
    return {
      name: en(props.name),
      url: en(props.url),
      temp: Number.isFinite(temp) ? Math.round(temp) : null,
      snow48: Math.round(snow48),
      snowPeriods,
      snowSoon,
      firstSnow,
      headline: first ? `${en(first.period.textForecastName)}: ${en(first.textSummary) || en(first.cloudPrecip) || ''}` : '',
      nowKind: first ? kind(en(first.abbreviatedForecast && first.abbreviatedForecast.textSummary)) : 'cloud',
      days,
    };
  };

  const load = async (loc) => {
    const key = `ss-eccc-v2-${loc}`;
    try {
      const cached = JSON.parse(localStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.t < CACHE_MINUTES * 60000) return cached.d;
    } catch (e) {}
    const res = await fetch(`${ECCC}${encodeURIComponent(loc)}?f=json&lang=en`);
    if (!res.ok) throw new Error(`ECCC ${res.status}`);
    const d = parse((await res.json()).properties || {});
    try {
      localStorage.setItem(key, JSON.stringify({ t: Date.now(), d }));
    } catch (e) {}
    return d;
  };

  class SsSnowWatch extends SsRail {
    connectedCallback() {
      super.connectedCallback();
      this.cards = [...this.querySelectorAll('.ss-snowcard[data-loc]')];
      this.powder = Number(this.dataset.powder || 15);
      const start = () => this.loadAll();
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.io.disconnect();
            start();
          }
        }, { rootMargin: '400px 0px' });
        this.io.observe(this);
      } else start();
    }

    async loadAll() {
      const results = await Promise.all(
        this.cards.map(async (card) => {
          const loc = card.dataset.loc;
          if (!loc) return this.fail(card), null;
          try {
            const d = await load(loc);
            this.fill(card, d);
            return { card, d };
          } catch (e) {
            this.fail(card);
            return null;
          }
        })
      );
      const ok = results.filter(Boolean);
      if (this.dataset.sort === 'true' && ok.length > 1) {
        const score = (r) => r.d.snow48 * 10 + r.d.snowPeriods;
        const track = this.querySelector('.ss-snowwatch__track');
        [...ok].sort((a, b) => score(b) - score(a)).forEach((r) => track.appendChild(r.card));
        this.cards.filter((c) => c.classList.contains('is-failed')).forEach((c) => track.appendChild(c));
        track.scrollTo({ left: 0, behavior: 'auto' });
        this.refresh();
      }
      const leader = [...ok].sort((a, b) => b.d.snow48 - a.d.snow48)[0];
      const leaderEl = this.closest('section').querySelector('[data-leader]');
      if (leaderEl && leader && leader.d.snow48 > 0) {
        leaderEl.textContent = `Most snow in the next 48 h: ${leader.card.querySelector('.ss-snowcard__name').textContent}, ${leader.d.snow48} cm`;
        leaderEl.hidden = false;
      }
    }

    fill(card, d) {
      const $ = (sel) => card.querySelector(sel);
      const trace = d.snow48 === 0 && d.snowSoon;
      $('[data-snow]').textContent = trace ? '<1' : d.snow48;
      $('[data-temp]').textContent = d.temp === null ? '–' : `${d.temp}°`;
      $('[data-icon]').innerHTML = ICONS[d.nowKind] || ICONS.cloud;
      $('[data-summary]').textContent = d.headline;
      $('[data-summary]').title = d.headline;
      $('[data-days]').innerHTML = d.days
        .map((day) => `<li class="ss-day ss-day--${day.kind}" title="${esc(day.summary)}"><span>${esc(day.name)}</span>${ICONS[day.kind]}<b>${day.temp === null ? '–' : esc(day.temp) + '°'}</b></li>`)
        .join('');
      const badge = $('[data-badge]');
      let label = '';
      if (d.snow48 >= this.powder) label = 'Powder alert';
      else if (d.snow48 >= 5) label = 'Snow incoming';
      else if (d.snow48 > 0) label = 'Light snow';
      else if (trace) label = 'Flurries';
      else if (d.firstSnow) {
        // e.g. "Thursday" -> "Snow Thursday", "Thursday night" -> "Snow Thu night"
        if (/^(today|tonight)$/i.test(d.firstSnow)) label = `Snow ${d.firstSnow.toLowerCase()}`;
        const night = /night/i.test(d.firstSnow);
        const day = d.firstSnow.replace(/\s*night/i, '').trim();
        if (!label) label = `Snow ${night ? day.slice(0, 3) + ' night' : day}`;
      }
      if (label) {
        badge.textContent = label;
        badge.hidden = false;
        card.classList.toggle('is-powder', d.snow48 >= this.powder);
      }
      card.classList.add('is-loaded');
    }

    fail(card) {
      card.classList.add('is-failed');
      const s = card.querySelector('[data-summary]');
      if (s) s.textContent = 'Forecast unavailable right now. Check weather.gc.ca.';
    }
  }
  if (!customElements.get('ss-snow-watch')) customElements.define('ss-snow-watch', SsSnowWatch);

  /* ---------- Reveal on scroll ---------- */
  const reveal = () => {
    const els = document.querySelectorAll('.ss-reveal:not(.is-in)');
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reveal);
  else reveal();
  document.addEventListener('shopify:section:load', reveal);
})();
